/**
 * The search field's suggestion menu (Slack's search filter chips; core logic/searchSuggest via SearchViewModel):
 * filters (`from:`, `in:`, …), then people, channels, `has:` / `is:` values and dates for the filter being typed. The
 * Search screen shows it in a Popover under its field; the field keeps the keyboard (↑ ↓ Enter Tab Esc go to the
 * view model). While a date filter (`before:`, `after:`, `on:`, `during:`) is being completed, a "Choose a date…"
 * row opens a calendar (DatePicker); `during:` can also take the shown month.
 */
import { useState } from "react";
import { SearchSuggest, type SearchSuggestMenu as Menu, type SearchSuggestion } from "@bundle/core";
import type { IconName } from "@bundle/tokens";
import { useLocale, useT, useTr } from "../i18n/I18nProvider.tsx";
import { Avatar, Button, DatePicker, Icon, Pressable, Stack, Text } from "../primitives/index.ts";

export type SearchSuggestMenuProps = {
  menu: Menu & { active: number };
  onChoose: (index: number) => void;
  /** The pointer moved onto a row (it becomes the highlighted one). */
  onHover: (index: number) => void;
  /** A date was picked for the date filter being completed ("YYYY-MM-DD"; `month`: the whole month, for `during:`). */
  onPickDate?: (date: string, month: boolean) => void;
  /** Overrides the menu's heading (the Mail box's own filters). */
  title?: string;
  testID: string;
};

const FILTER_ICONS: Record<string, IconName> = {
  from: "user",
  with: "users",
  in: "hash",
  to: "mail",
  domain: "globe",
  subject: "file",
  label: "tag",
  or: "filter",
  has: "filter",
  is: "thread",
  before: "calendar",
  after: "calendar",
  on: "calendar",
  during: "calendar",
};

const HAS_ICONS: Record<string, IconName> = { link: "link", file: "file", pin: "pin", reaction: "reaction" };

function iconFor(item: SearchSuggestion): IconName {
  // The Mail box's `in:` (labels and folders) and its domain rows.
  if (item.kind === "value" && item.description?.key === "mail.search.value.label") return "tag";
  if (item.kind === "value" && item.description?.key === "mail.search.value.folder") return "folder";
  if (item.kind === "value" && item.description?.key === "mail.search.value.domain") return "globe";
  if (item.kind === "value" && item.key === "has") return HAS_ICONS[item.value] ?? "filter";
  return FILTER_ICONS[item.key] ?? "search";
}

export function SearchSuggestMenu({ menu, onChoose, onHover, onPickDate, title: heading, testID }: SearchSuggestMenuProps) {
  const t = useT();
  const locale = useLocale();
  const [picking, setPicking] = useState<string>();
  const [shown, setShown] = useState<string>();
  const dateKey = menu.key !== undefined && (SearchSuggest.SEARCH_DATE_KEYS as string[]).includes(menu.key) && onPickDate !== undefined ? menu.key : undefined;
  const title = heading !== undefined
    ? heading
    : menu.exclude
    ? t("search.suggest.exclude")
    : menu.key === undefined
      ? t("search.suggest.filters")
      : menu.key === "from" || menu.key === "with"
        ? t("search.suggest.people")
        : menu.key === "in"
          ? t("search.suggest.conversations")
          : t("search.suggest.values");
  return (
    <Stack gap="0.5" padding="1" testID={testID}>
      <Stack paddingX="2" paddingY="1">
        <Text variant="caption" weight="semibold" color="text.tertiary">
          {title}
        </Text>
      </Stack>
      <Stack gap="0.5" accessibilityRole="listbox" accessibilityLabel={title}>
        {menu.items.map((item, i) => (
          <Pressable
            key={item.insert}
            direction="horizontal"
            radius="sm"
            highlighted={i === menu.active}
            onPress={() => onChoose(i)}
            onHover={() => onHover(i)}
            testID={`${testID}-${item.insert}`}
          >
            <SuggestionRow item={item} />
          </Pressable>
        ))}
      </Stack>
      {dateKey === undefined ? null : picking !== dateKey ? (
        <Pressable direction="horizontal" radius="sm" onPress={() => setPicking(dateKey)} testID={`${testID}-pick-date`}>
          <Stack grow direction="horizontal" align="center" gap="2.5" paddingX="2" paddingY="1.5">
            <Stack padding="1" radius="sm" border="border.subtle" background="bg.canvas">
              <Icon name="calendar" size="icon.sm" color="text.secondary" />
            </Stack>
            <Text variant="footnote" weight="semibold" lines={1}>
              {t("search.value.pickDate")}
            </Text>
          </Stack>
        </Pressable>
      ) : (
        <Stack gap="1" paddingX="2" paddingY="1" testID={`${testID}-calendar`}>
          <DatePicker
            onChange={(day) => onPickDate?.(day, false)}
            onMonthChange={setShown}
            locale={locale === "pt-BR" ? "pt-BR" : "en"}
            testID={`${testID}-datepicker`}
          />
          {dateKey === "during" ? (
            <Button
              variant="secondary"
              size="sm"
              label={t("search.value.pickMonth")}
              onPress={() => onPickDate?.(shown ?? new Date().toISOString().slice(0, 10), true)}
              testID={`${testID}-pick-month`}
            />
          ) : null}
        </Stack>
      )}
    </Stack>
  );
}

function SuggestionRow({ item }: { item: SearchSuggestion }) {
  const tr = useTr();
  const detail = item.kind === "person" ? item.detail : item.kind === "filter" || item.kind === "value" ? (item.description === undefined ? undefined : tr(item.description)) : undefined;
  return (
    <Stack grow direction="horizontal" align="center" gap="2.5" paddingX="2" paddingY="1.5">
      {item.kind === "person" ? (
        <Avatar name={item.label} src={item.avatarUrl} size="avatar.sm" />
      ) : (
        <Stack padding="1" radius="sm" border="border.subtle" background="bg.canvas">
          <Icon name={item.kind === "channel" ? (item.isPrivate ? "lock" : "hash") : iconFor(item)} size="icon.sm" color="text.secondary" />
        </Stack>
      )}
      <Stack grow direction="horizontal" align="center" gap="2">
        <Text variant="footnote" weight="semibold" lines={1}>
          {item.label}
        </Text>
        {detail === undefined ? null : (
          <Stack grow>
            <Text variant="footnote" color="text.tertiary" lines={1}>
              {detail}
            </Text>
          </Stack>
        )}
      </Stack>
    </Stack>
  );
}
